/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";

export const GetTableByIdParams = z.object({
  table_id: z
    .string()
    .describe(
      "The unique identifier for the table you want to retrieve details for",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Getting table info...' or 'Loading table data...'",
    ),
});

export type GetTableByIdParamsType = z.infer<typeof GetTableByIdParams>;

export const getTableByIdExecute = async (rawArgs: any) => {
  let params: GetTableByIdParamsType;
  try {
    params = GetTableByIdParams.parse(rawArgs || {});
  } catch (error) {
    console.error("Error parsing arguments for getTableById:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for get_table_by_id. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const { table_id } = params;

  const proxyApiUrl = `/api/canvasui/tables/${table_id}/fields`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for get_table_by_id (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for get_table_by_id (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          errorDetailString = `Proxy error for get_table_by_id (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
        }
      } catch {
        errorDetailString = `Proxy error for get_table_by_id (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[GET_TABLE_BY_ID_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[GET_TABLE_BY_ID_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error(
      "[GET_TABLE_BY_ID_TOOL] Error in getTableByIdExecute:",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error fetching table: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const getTableByIdDescription = `Retrieves the detailed field configuration of a specific table by its ID.
This tool provides information about all the fields in a table, including their names, types, and other metadata.

IMPORTANT:
- The output includes detailed information about each field in the table.
- This information is useful for understanding table structure and for creating or updating table records.
- Field information includes field types, names, and other configuration details.

Required parameters:
- table_id: The ID of the table to fetch field information for.
    - If the user mentions a table by name on the canvas, try to find its ID in 'additionalData.canvas.nodes' (look for a node with a matching label and use its 'data.tableId' or 'data.table_id' property).
    - If the Table ID is directly provided or known, use that.

Output:
- The tool returns a JSON object representing the table's field configuration.
- This includes all field definitions with their types, names, and metadata.`;

export const getTableByIdAtom = atom<
  JotaiTool<z.infer<typeof GetTableByIdParams>>
>(() => ({
  name: "get_table_by_id",
  description: getTableByIdDescription,
  parameters: GetTableByIdParams,
  execute: atom(null, async (_get, _set, { args }) => {
    return getTableByIdExecute(args);
  }),
}));
getTableByIdAtom.debugLabel = "getTableByIdAtom";
/* eslint-enable sort-exports/sort-exports */
